import { useEffect, useState } from "react";
import { Maximize2, Minimize2, Minus, X } from "@/shared/ui/icons";
import { useTranslation } from "react-i18next";

import { useIsMobile } from "@/core/hooks";
import { Button, MARQUE } from "@dozka/ui";

import { AccountScopeSwitcher } from "./AccountScopeSwitcher";
import { DemoBadge } from "./DemoBridge";
import { AppInstallButton } from "./AppInstallButton";
import { HardRefreshButton } from "./HardRefreshButton";
import { RangBadge } from "./RangBadge";
import { useSansBarreDuHaut } from "./SideRail";
import { UserBadge } from "./UserBadge";

/**
 * Chrome haut du cockpit. Rend toujours (Tauri ET web) — le profil utilisateur
 * partage la ligne avec le titre + (en Tauri non-mac) les boutons fenetre,
 * pour eviter une ligne dediee au profil seul.
 *
 * Detection runtime : `window.__TAURI_INTERNALS__` est injecte par Tauri au
 * boot. Plus fiable que `import.meta.env.TAURI_*` qui n'est defini qu'a la
 * build (et ne distingue pas dev-web vs dev-tauri).
 *
 * Layouts :
 *   - Tauri Windows/Linux : drag + titre | UserBadge | min / max / close
 *   - Tauri macOS         : pad traffic-lights (overlay) + drag + titre | UserBadge
 *   - Web (browser pur)   : titre | UserBadge (pas de drag, pas de boutons)
 *
 * Cote Tauri :
 *   - `decorations: false` (Windows/Linux) ou `TitleBarStyle::Overlay` (mac)
 *     est applique dans `cyrano-desktop/src-tauri/src/app/window.rs` quand
 *     `pake.json` a `hide_title_bar: true`.
 *   - L'app entiere doit fournir une drag region — sinon impossible de
 *     deplacer la fenetre. C'est le `data-tauri-drag-region` du header ici.
 *
 * macOS : on rend SEULEMENT le titre + drag region (les traffic-lights natifs
 * sont conserves via TitleBarStyle::Overlay → ils flottent par-dessus). Sur
 * Windows/Linux on rend en plus les boutons min/max/close custom.
 */
export function TitleBar() {
  const { t } = useTranslation();
  const tauri = useTauri();
  const isMaximized = tauri?.isMaximized ?? false;
  const isFullscreen = tauri?.isFullscreen ?? false;
  const isMobile = useIsMobile();

  // En plein écran, on RETIRE la drag region. Déplacer une fenêtre fullscreen
  // (startDragging) la fait sortir en fenêtre flottante NON redimensionnable —
  // le flag fullscreen reste posé, le bord de resize (WS_THICKFRAME) a disparu
  // → « fenêtre en grand qu'on ne peut plus rétrécir, coins morts ». Pas de
  // drag region = bug impossible. (F11 reste le moyen d'entrer/sortir, et
  // re-presser F11 récupère une fenêtre déjà coincée.)
  const dragRegion = isFullscreen ? {} : { "data-tauri-drag-region": "" };

  // Sur le web : plus de barre du haut, tout est dans le rail (cf SideRail).
  // Web desktop : aucune barre du haut (le rail porte tout). App de bureau :
  // la barre reste, vide — poignée + boutons de fenêtre en haut à droite.
  const sansBarreDuHaut = useSansBarreDuHaut();
  if (sansBarreDuHaut) return null;

  return (
    <div
      {...dragRegion}
      data-titlebar
      // z-[300] cree un stacking context qui isole les popovers internes
      // (UserBadge dropdown, etc.) au-dessus des overlays internes au shell
      // (CreatorOnboardingWizard z-[200], modals z-[100]). Sans ca, les
      // popovers sont visuellement caches par le wizard meme si la barre
      // elle-meme reste visible.
      //
      // `data-titlebar` : ancre les overrides safe-area mobile (cf globals.css).
      // Sur iOS avec apple-mobile-web-app-status-bar-style=black-translucent +
      // viewport-fit=cover, la status bar (heure / dynamic island) chevauche
      // le contenu. On reserve env(safe-area-inset-top) en mobile-only.
      className={
        isMobile
          // Mobile : PAS de h-12 ici — la hauteur vient du CSS override
          // `[data-titlebar] { height: calc(3rem + safe-area-inset-top) }`
          // dans globals.css. Si on met h-12, Tailwind (layer utilities)
          // bat le CSS (layer components) et la barre fait 48px alors que
          // le padding-top safe-area pousse les icones en dehors.
          ? "mobile-bar relative z-[300] flex shrink-0 items-center justify-between border-b border-fg/[0.07] pl-3 pr-2 select-none"
          : "relative z-[300] flex h-9 shrink-0 items-center justify-between border-b border-fg/[0.07] bg-panel/60 pl-3 pr-1.5 backdrop-blur-2xl backdrop-saturate-150 select-none"
      }
    >
      <div
        {...dragRegion}
        className="pointer-events-none flex flex-1 items-center gap-2 text-[11px] font-medium text-muted"
      >
        {tauri?.isMac && <div className="w-16" />}
        {isMobile ? (
          <>
            <img
              src={MARQUE.icone}
              alt={MARQUE.id === "dozka" ? "Dozka" : MARQUE.nom}
              className="pointer-events-auto h-6 w-6"
            />
            {/* Le nom ne s'affiche qu'à partir de 640 px : sur un téléphone, la place va aux boutons. */}
            <span className="hidden text-[13px] font-semibold text-fg sm:inline">
              {MARQUE.nom}
            </span>
          </>
        ) : (
          // App de bureau : le petit logo, sans nom — la barre n'est qu'une
          // poignée (Kayla, 08-10-2026 : « c'était celui où il y avait le
          // texte que je voulais garder, mais sans le texte »). Le rail, lui,
          // ne le répète pas sous cette barre.
          <img
            src={MARQUE.icone}
            alt={MARQUE.id === "dozka" ? "Dozka" : MARQUE.nom}
            className="pointer-events-auto h-5 w-5"
            {...dragRegion}
          />
        )}
      </div>

      {/* Le profil, l'organisation, le rang et le téléchargement ne vivent
          plus ici sur desktop depuis le 08-10-2026 (Kayla : « le truc en
          haut : le profil, l'organisation et tout, qui servait à rien ») —
          ils sont dans le rail. L'app de bureau garde la barre comme
          poignée + boutons de fenêtre, et rien d'autre. */}
      {isMobile && (
        <div className="flex shrink-0 items-center gap-1 pr-2">
          <DemoBadge />
          <AppInstallButton />
          <HardRefreshButton />
          <AccountScopeSwitcher />
          <RangBadge />
          <UserBadge />
        </div>
      )}

      {/* Boutons fenetre custom — uniquement sur Tauri non-mac. En plein écran,
          min/max n'ont pas de sens (et déclencheraient le même état coincé) →
          on ne garde que Fermer ; F11 sort du plein écran. */}
      {tauri && !tauri.isMac && (
        <div className="flex h-full items-center gap-0.5">
          {!isFullscreen && (
            <>
              <Button
                variant="ghost"
                size="md"
                iconOnly
                aria-label={t("shell.titleBar.minimize")}
                onClick={() => void tauri.win.minimize()}
                icon={<Minus className="h-3.5 w-3.5" />}
              />
              <Button
                variant="ghost"
                size="md"
                iconOnly
                aria-label={isMaximized ? t("shell.titleBar.restore") : t("shell.titleBar.maximize")}
                onClick={() => void tauri.win.toggleMaximize()}
                icon={isMaximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              />
            </>
          )}
          <Button
            variant="ghost"
            size="md"
            iconOnly
            className="hover:text-erreur"
            aria-label={t("shell.titleBar.close")}
            onClick={() => void tauri.win.close()}
            icon={<X className="h-3.5 w-3.5" />}
          />
        </div>
      )}
    </div>
  );
}

/** La fenêtre Tauri et son état, ou `null` sur le web. */
function useTauri(): (TauriRuntime & { isMaximized: boolean; isFullscreen: boolean }) | null {
  const [tauri, setTauri] = useState<TauriRuntime | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("__TAURI_INTERNALS__" in window)) return;
    let unlisten: (() => void) | undefined;
    void import("@tauri-apps/api/window").then((mod) => {
      const win = mod.getCurrentWindow();
      const isMac = navigator.platform.toLowerCase().includes("mac");
      setTauri({ win, isMac });
      const sync = () => {
        void win.isMaximized().then(setIsMaximized);
        void win.isFullscreen().then(setIsFullscreen);
      };
      sync();
      void win
        .onResized(() => sync())
        .then((u) => {
          unlisten = u;
        });
    });
    return () => {
      if (unlisten) unlisten();
    };
  }, []);
  return tauri ? { ...tauri, isMaximized, isFullscreen } : null;
}

interface TauriRuntime {
  win: import("@tauri-apps/api/window").Window;
  isMac: boolean;
}
